import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { ChevronLeft, Plus, Trash2, Loader2, Rss, Sparkles, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function FeedsSettings() {
  const [feeds, setFeeds] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showSuggest, setShowSuggest] = useState(false);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [adding, setAdding] = useState({});
  const [bulkLoading, setBulkLoading] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    try {
      const [fRes, sRes] = await Promise.all([
        api.get('/feeds'),
        api.get('/feeds/suggestions'),
      ]);
      setFeeds(fRes.data);
      setSuggestions(sRes.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/feeds', { name, url });
      setName(''); setUrl(''); setShowForm(false);
      toast.success('Flux ajouté');
      await load();
    } catch (e) { toast.error('Échec'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer ce flux ?')) return;
    try {
      await api.delete(`/feeds/${id}`);
      toast.success('Flux supprimé');
      await load();
    } catch (e) { toast.error('Échec'); }
  };

  const addSuggestion = async (s) => {
    setAdding((a) => ({ ...a, [s.url]: true }));
    try {
      await api.post('/feeds', { name: s.name, url: s.url });
      toast.success(`${s.name} ajouté`);
      await load();
    } catch (e) {
      toast.error('Échec');
    } finally {
      setAdding((a) => ({ ...a, [s.url]: false }));
    }
  };

  const addAllMissing = async () => {
    const missing = suggestions.filter((s) => !s.already_added);
    if (missing.length === 0) { toast.info('Tous les flux recommandés sont déjà ajoutés'); return; }
    setBulkLoading(true);
    try {
      const { data } = await api.post('/feeds/bulk', missing.map(({ name, url }) => ({ name, url })));
      toast.success(`${data.added} flux ajoutés`);
      await load();
    } catch (e) {
      toast.error('Échec de l\'ajout groupé');
    } finally { setBulkLoading(false); }
  };

  return (
    <div className="mobile-shell pb-8">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-slate-100 px-4 py-3 flex items-center justify-between">
        <button onClick={() => nav('/settings')} className="flex items-center gap-1 text-slate-700 font-medium" data-testid="btn-back">
          <ChevronLeft size={22} /> Retour
        </button>
        <h1 className="font-bold text-slate-900">Flux RSS</h1>
        <button onClick={() => setShowForm(true)} data-testid="btn-add-feed" className="text-blue-600 font-semibold">
          <Plus size={18} />
        </button>
      </header>

      <div className="px-5 pt-5 space-y-3">
        {loading && <Loader2 className="mx-auto animate-spin text-blue-600" />}

        {!loading && (
          <button
            onClick={() => setShowSuggest((s) => !s)}
            className="w-full bg-gradient-to-r from-blue-50 to-orange-50 border-2 border-blue-200 rounded-2xl p-4 flex items-center gap-3 hover:shadow-md transition"
            data-testid="btn-toggle-suggestions"
          >
            <div className="w-10 h-10 rounded-xl bg-white text-blue-600 flex items-center justify-center flex-shrink-0">
              <Sparkles size={20} />
            </div>
            <div className="flex-1 text-left">
              <div className="font-semibold text-slate-900">Flux recommandés</div>
              <div className="text-xs text-slate-600">14 grands médias français en 1 clic</div>
            </div>
            <span className="text-xs font-bold text-blue-600">
              {showSuggest ? '—' : '+'}
            </span>
          </button>
        )}

        {showSuggest && !loading && (
          <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2" data-testid="suggestions-list">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-bold text-slate-900 text-sm">Médias français</h3>
              <button
                onClick={addAllMissing}
                disabled={bulkLoading || suggestions.every((s) => s.already_added)}
                className="text-xs font-semibold text-blue-600 hover:text-blue-700 disabled:opacity-50 flex items-center gap-1"
                data-testid="btn-add-all-suggestions"
              >
                {bulkLoading && <Loader2 size={12} className="animate-spin" />}
                Tout ajouter
              </button>
            </div>

            {(() => {
              const REGIONS = [
                { key: 'general', label: '🇫🇷 Presse généraliste française', color: 'text-slate-700', bg: 'bg-slate-100' },
                { key: 'outremer', label: '🌴 Outre-mer', color: 'text-green-800', bg: 'bg-green-100' },
                { key: 'afrique', label: '🌍 Afrique', color: 'text-red-800', bg: 'bg-red-100' },
              ];
              return REGIONS.map((reg) => {
                const group = suggestions.filter((s) => (s.region || 'general') === reg.key);
                if (group.length === 0) return null;
                return (
                  <div key={reg.key} className="mb-3" data-testid={`suggestions-group-${reg.key}`}>
                    <div className={`text-xs font-bold uppercase tracking-wide px-2 py-1.5 rounded-md mb-1 ${reg.color} ${reg.bg}`}>
                      {reg.label} <span className="font-normal opacity-70">({group.length})</span>
                    </div>
                    {group.map((s) => (
                      <div key={s.url}
                        className={`flex items-center gap-3 py-2 px-2 border-b border-slate-100 last:border-0 rounded ${
                          reg.key === 'outremer' ? 'hover:bg-green-50'
                          : reg.key === 'afrique' ? 'hover:bg-red-50'
                          : 'hover:bg-slate-50'
                        }`}
                        data-testid={`suggestion-${s.name}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-slate-900 text-sm">{s.name}</div>
                          <div className="text-[11px] text-slate-400 truncate">{s.website}</div>
                        </div>
                        {s.already_added ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600 px-2 py-1 rounded-md bg-green-50">
                            <Check size={12} /> Ajouté
                          </span>
                        ) : (
                          <button
                            onClick={() => addSuggestion(s)}
                            disabled={adding[s.url]}
                            data-testid="btn-add-suggestion"
                            className={`text-xs font-semibold text-white px-3 py-1.5 rounded-lg flex items-center gap-1 disabled:opacity-60 ${
                              reg.key === 'outremer' ? 'bg-green-600 hover:bg-green-700'
                              : reg.key === 'afrique' ? 'bg-red-600 hover:bg-red-700'
                              : 'bg-blue-600 hover:bg-blue-700'
                            }`}
                          >
                            {adding[s.url] ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
                            Ajouter
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                );
              });
            })()}
          </div>
        )}

        {!loading && feeds.length === 0 && !showForm && (
          <div className="py-12 text-center">
            <div className="mx-auto w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mb-3">
              <Rss className="text-blue-600" />
            </div>
            <p className="text-slate-700 font-semibold">Aucun flux</p>
            <p className="text-sm text-slate-500">Ajoutez l'URL d'un flux RSS.</p>
          </div>
        )}

        {feeds.map((f) => {
          // Identify region by matching URL with suggestions
          const sug = suggestions.find((s) => s.url === f.url);
          const region = sug?.region || 'general';
          const isOM = region === 'outremer';
          const isAF = region === 'afrique';
          return (
          <div
            key={f.id}
            className={`border rounded-2xl p-4 flex items-center gap-3 ${
              isOM ? 'bg-green-50 border-green-300'
              : isAF ? 'bg-red-50 border-red-300'
              : 'bg-white border-slate-200'
            }`}
            data-testid={`feed-${f.id}`}
          >
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
              isOM ? 'bg-green-100 text-green-700'
              : isAF ? 'bg-red-100 text-red-700'
              : 'bg-blue-50 text-blue-600'
            }`}>
              <Rss size={18} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-slate-900 truncate flex items-center gap-1.5">
                {f.name}
                {isOM && <span className="text-[9px] font-bold uppercase tracking-wide bg-green-600 text-white px-1.5 py-0.5 rounded">🌴 OM</span>}
                {isAF && <span className="text-[9px] font-bold uppercase tracking-wide bg-red-600 text-white px-1.5 py-0.5 rounded">🌍 AF</span>}
              </div>
              <div className="text-xs text-slate-500 truncate">{f.url}</div>
            </div>
            <button onClick={() => remove(f.id)} data-testid={`btn-delete-feed-${f.id}`} className="p-2 text-red-500 hover:bg-red-50 rounded-lg">
              <Trash2 size={16} />
            </button>
          </div>
          );
        })}

        {showForm && (
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3" data-testid="feed-form">
            <h2 className="font-bold text-slate-900">Ajouter un flux RSS</h2>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">Nom</label>
              <input type="text" required value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Le Monde - Une"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none"
                data-testid="feed-field-name" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-500 mb-1.5 uppercase tracking-wide">URL RSS</label>
              <input type="url" required value={url} onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com/rss.xml"
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-blue-600 outline-none"
                data-testid="feed-field-url" />
            </div>
            <div className="flex gap-2 pt-2">
              <button type="button" onClick={() => setShowForm(false)} className="flex-1 border-2 border-slate-200 py-3 rounded-xl font-semibold text-slate-600" data-testid="btn-cancel-feed">
                Annuler
              </button>
              <button type="submit" disabled={saving} className="flex-1 bg-blue-600 text-white py-3 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="btn-save-feed">
                {saving ? <Loader2 size={16} className="animate-spin" /> : null} Ajouter
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
